import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/api/client';

export default function BoxCard({ box, onEdit }) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => api.entities.ShippingBox.delete(box.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      toast.success('Box removed');
    },
    onError: (error) => toast.error(error.message ?? 'Failed to remove box'),
  });

  const handleDelete = () => {
    // Archived rather than deleted, so past orders keep showing which box they
    // shipped in. Worth saying out loud — "remove" implying data loss would be
    // a worse surprise than a slightly wordy prompt.
    if (window.confirm(`Remove "${box.name}"? Past orders that used it are unaffected.`)) {
      deleteMutation.mutate();
    }
  };

  return (
    <Card className="border-slate-200 hover:border-purple-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
              <Box className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">{box.name}</h4>
              <p className="text-sm text-slate-600">
                {box.length}&quot; × {box.width}&quot; × {box.height}&quot;
              </p>
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(box)}
              className="hover:bg-purple-50 hover:text-purple-600"
              aria-label={`Edit ${box.name}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="hover:bg-red-50 hover:text-red-600"
              aria-label={`Remove ${box.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
